// ─────────────────────────────────────────────────────────────────────────
// timezone — the application's clock.
//
// THE REQUIREMENT
// "We work with multiple locations, so we need to see the time based on
// location. Set up a common time setup like GST, UTC, IST — list all the time
// zones, and if I select the time zone the entire application has to work on
// that time zone. It won't reflect the system time."
//
// So: one tenant-wide zone, chosen in admin, used for RENDERING every
// date-time AND for interpreting what the user types — never the browser's.
//
// WHERE THE SETTING LIVES
// detailViewDefaults.timeZone, alongside dateFormat / phoneFormat / the empty
// texts. That store already exists, is already tenant-scoped, and is already
// loaded once at startup — a second settings store would only create a second
// thing to keep in sync.
//
// THE ZONE LIST IS NOT HARDCODED
// It comes from Intl.supportedValuesOf('timeZone') — every IANA zone the
// browser knows. The abbreviations the requirement names (IST, GST, UTC) are
// not IANA identifiers, so they are provided as an admin-editable ALIAS map
// (detailViewDefaults.timeZoneAliases) that labels the real zones. Nothing in
// this file enumerates a country.
//
// ── THE CALENDAR-DATE TRAP (the important part) ──────────────────────────
// A date of birth, a passport expiry, an education start date are CALENDAR
// dates: "7 July 2026" means the same thing in Dubai and in New York. Passing
// one through a timezone conversion shifts it by a day for half the world's
// zones — silently corrupting data that was never about an instant in time.
//
// So conversion is OPT-IN, never blanket: only `datetime`/`time` fields, or a
// field explicitly marked `tzAware`, are converted. Plain `date` fields keep
// their calendar semantics. See shouldConvertToZone below.
// ─────────────────────────────────────────────────────────────────────────
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';
import { getDefaults } from './detailDefaults';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

// The browser's own zone, used when the tenant has not chosen one. Resolved
// lazily and cached: dayjs.tz.guess() reads Intl on every call.
let guessed = null;
function browserZone() {
  if (guessed === null) {
    try {
      guessed = dayjs.tz.guess() || 'UTC';
    } catch {
      guessed = 'UTC';
    }
  }
  return guessed;
}

/**
 * isValidZone — is this a zone we are willing to run the application on?
 *
 * Deliberately STRICTER than Intl. ICU accepts bare abbreviations, but does so
 * inconsistently and with traps that would be invisible until a DST boundary:
 *
 *   'IST' → Asia/Calcutta   (yet IST is equally Irish and Israel Standard Time)
 *   'EST' → America/Panama  (a fixed -05:00 that NEVER shifts to EDT, so a
 *                            tenant picking "EST" would silently be an hour
 *                            wrong for two-thirds of the year)
 *   'GST' → rejected entirely
 *
 * So an accepted zone must be a real IANA identifier — "Area/Location", or the
 * one legitimate bare name, UTC. Abbreviations remain available to users as
 * LABELS through the alias map, where they are unambiguous because they point
 * at a specific IANA zone.
 */
export function isValidZone(zone) {
  const name = String(zone ?? '').trim();
  if (!name) return false;
  if (name !== 'UTC' && !name.includes('/')) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: name });
    return true;
  } catch {
    return false;
  }
}

/**
 * getAppTimeZone — the zone everything renders and is interpreted in.
 *
 * An unset or unknown value falls back to the browser's zone, so the app keeps
 * working exactly as it did before anyone configured this. Written so a
 * per-user override can be layered in later without touching any call site.
 */
export function getAppTimeZone(defaults = getDefaults()) {
  const configured = String(defaults?.timeZone ?? '').trim();
  return isValidZone(configured) ? configured : browserZone();
}

/** appNow — "now", in the application's zone. */
export function appNow(defaults) {
  return dayjs().tz(getAppTimeZone(defaults));
}

/**
 * toApp — read an instant (ISO string, Date, dayjs, epoch ms) as it appears in
 * the application's zone. Invalid input returns an invalid dayjs, so callers
 * can keep using .isValid() exactly as they do now.
 */
export function toApp(value, defaults) {
  const parsed = dayjs(value?.$date ?? value);
  return parsed.isValid() ? parsed.tz(getAppTimeZone(defaults)) : parsed;
}

/**
 * formatApp — the one formatter. Falls back to the tenant's configured
 * dateFormat, so changing the format still happens in exactly one place.
 */
export function formatApp(value, format, defaults = getDefaults()) {
  const d = toApp(value, defaults);
  if (!d.isValid()) return '';
  return d.format(format || defaults?.dateFormat || 'DD MMM YYYY');
}

/**
 * tzShortLabel — the small suffix printed after a TIME so a reader knows which
 * clock they are looking at: "IST", "GST", or the zone's own name when nobody
 * has given it a short one.
 *
 * Deliberately NOT printed after a plain calendar date. A date of birth has no
 * time and no zone; stamping one on it claims a precision the value does not
 * have.
 */
export function tzShortLabel(defaults = getDefaults(), at) {
  const zone = getAppTimeZone(defaults);
  const alias = aliasFor(zone, defaults);
  // A zone that changes abbreviation across the year is stored as a PAIR
  // ("EST/EDT"), because the table cannot know which applies. Given the instant
  // being printed we can: a timestamp reading "12:37 AM EST/EDT" tells the
  // reader the two things it might be and leaves them to work out which, which
  // is precisely the ambiguity the suffix exists to remove.
  if (alias && alias.includes('/')) {
    const inEffect = zoneAbbreviation(zone, at);
    if (inEffect) {
      const halves = alias.split('/').map((half) => half.trim());
      const matched = halves.find((half) => half.toUpperCase() === inEffect.toUpperCase());
      return matched || inEffect;
    }
    // No abbreviation available: the pair is still more informative than the
    // raw zone name, so it stands.
  }
  return alias || zoneAbbreviation(zone, at) || zone;
}

/**
 * zoneAbbreviation — the short name a zone actually goes by AT a given instant
 * ("EDT" in August, "EST" in January), or '' when it has no letter form.
 *
 * Intl answers this correctly including the daylight-saving rules, which is why
 * it is asked rather than a lookup table: the rules change, and a table that
 * says "EST/EDT" is a table admitting it does not know.
 *
 * Zones with no common abbreviation come back as an offset ("GMT+5:30"). Those
 * are rejected here so the caller falls through to its configured alias — India
 * is written "IST", never "GMT+5:30".
 */
export function zoneAbbreviation(zone, at) {
  try {
    const when = at === undefined ? new Date() : new Date(dayjs(at?.$date ?? at).valueOf());
    if (Number.isNaN(when.valueOf())) return '';
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'short',
    }).formatToParts(when);
    const name = parts.find((part) => part.type === 'timeZoneName')?.value ?? '';
    // Letters only. "GMT+5:30" is an offset wearing a name badge.
    return /^[A-Za-z]+$/.test(name) ? name : '';
  } catch {
    // An unknown zone throws rather than guessing. The caller has a fallback.
    return '';
  }
}

/**
 * formatAppDateTime — an INSTANT, in the tenant's zone, with the zone named.
 *
 * "Aug 06, 2026 | 07:03 PM IST"
 *
 * The format and whether to show the zone are both config, so a tenant can
 * change either without a deploy. The zone suffix is what turns an ambiguous
 * timestamp into a fact: without it, a team spread across countries cannot tell
 * whether 07:03 PM is theirs or someone else's.
 */
export function formatAppDateTime(value, defaults = getDefaults()) {
  const shown = formatApp(value, defaults?.dateTimeFormat || 'MMM DD, YYYY | hh:mm A', defaults);
  if (!shown) return '';
  if (defaults?.showTimeZoneLabel === false) return shown;
  // The label is resolved FOR THIS INSTANT, so a summer timestamp reads "EDT"
  // and a winter one "EST" — rather than both reading "EST/EDT".
  const label = tzShortLabel(defaults, value);
  return label ? `${shown} ${label}` : shown;
}

/**
 * shouldConvertToZone — may this field's value be moved between zones?
 *
 * FALSE for plain calendar dates. This is the guard described in the header,
 * and it is deliberately conservative: a field must SAY it carries an instant
 * (type datetime/time) or opt in with `tzAware`, otherwise it is left alone.
 * Being wrong in this direction shows a time in the wrong zone; being wrong in
 * the other direction changes a stored date by a day.
 */
export function shouldConvertToZone(field) {
  if (!field) return false;
  if (field.tzAware === true) return true;
  if (field.tzAware === false) return false;
  const type = String(field.type ?? '').toLowerCase();
  return type === 'datetime' || type === 'time' || type === 'datetime-local';
}

/**
 * fromAppInput — a picker value the user entered MEANING the application's
 * zone, converted to the correct absolute instant for storage.
 *
 * A DatePicker hands back a dayjs in the BROWSER's zone. If the tenant zone is
 * Asia/Dubai and the user picks 09:00, they mean 09:00 in Dubai — storing the
 * browser's 09:00 would be a different moment entirely.
 */
export function fromAppInput(value, defaults) {
  // An EMPTY input is not a moment, and must never become one.
  //
  // dayjs(undefined) returns the CURRENT time and reports isValid() === true —
  // so without this guard an untouched picker was serialised as "now". On the
  // submission form that wrote the moment of saving into interviewAvailability
  // .startTime AND .endTime, giving every submission an interview slot the
  // recruiter never entered, with both ends identical. dayjs(null) and
  // dayjs('') are correctly invalid; only `undefined` has this behaviour, which
  // is exactly the shape a registered-but-empty antd picker hands back.
  if (value === undefined || value === null || value === '') return null;
  const d = dayjs(value);
  if (!d.isValid()) return null;
  const zone = getAppTimeZone(defaults);
  // Re-interpret the WALL-CLOCK reading in the target zone, rather than
  // converting the instant (which would keep the wrong moment and merely
  // relabel it).
  return dayjs.tz(d.format('YYYY-MM-DDTHH:mm:ss'), zone);
}

/**
 * zoneOffsetLabel — "UTC+05:30" for a zone, at the current moment.
 * Computed rather than tabulated, so it stays correct across DST.
 */
export function zoneOffsetLabel(zone = getAppTimeZone()) {
  try {
    const minutes = dayjs().tz(zone).utcOffset();
    const sign = minutes < 0 ? '-' : '+';
    const abs = Math.abs(minutes);
    const hh = String(Math.floor(abs / 60)).padStart(2, '0');
    const mm = String(abs % 60).padStart(2, '0');
    return `UTC${sign}${hh}:${mm}`;
  } catch {
    return '';
  }
}

/**
 * tzLabel — what a viewer sees next to a time so they know WHICH zone they are
 * reading: "IST (UTC+05:30)" when an alias names it, else
 * "Asia/Kolkata (UTC+05:30)".
 */
export function tzLabel(defaults = getDefaults()) {
  const zone = getAppTimeZone(defaults);
  const alias = aliasFor(zone, defaults);
  return `${alias || zone} (${zoneOffsetLabel(zone)})`;
}

// Built-in aliases covering the abbreviations the requirement names, plus the
// common business zones. Admin-editable via detailViewDefaults.timeZoneAliases;
// anything configured there wins, and unknown zones simply have no alias.
export const BUILT_IN_TZ_ALIASES = Object.freeze({
  UTC: 'UTC',
  'Asia/Kolkata': 'IST',
  'Asia/Calcutta': 'IST',
  'Asia/Dubai': 'GST',
  'America/New_York': 'EST/EDT',
  'America/Chicago': 'CST/CDT',
  'America/Denver': 'MST/MDT',
  'America/Los_Angeles': 'PST/PDT',
  'Europe/London': 'GMT/BST',
  'Europe/Berlin': 'CET/CEST',
  'Asia/Singapore': 'SGT',
  'Asia/Tokyo': 'JST',
  'Australia/Sydney': 'AEST/AEDT',
});

/** aliasFor — the short name for a zone, config first. */
export function aliasFor(zone, defaults = getDefaults()) {
  const configured = defaults?.timeZoneAliases ?? {};
  return configured[zone] ?? BUILT_IN_TZ_ALIASES[zone] ?? '';
}

/**
 * listTimeZones — every zone the runtime knows, labelled with its alias and
 * current offset, sorted by offset then name so the picker reads like a map
 * rather than an alphabetical wall.
 *
 * Returns [{ value, label, alias, offsetLabel, offsetMinutes }].
 */
export function listTimeZones(defaults = getDefaults()) {
  let enumerated = [];
  try {
    enumerated = Intl.supportedValuesOf('timeZone') ?? [];
  } catch {
    // Older runtimes cannot enumerate at all; the union below still yields the
    // named zones, so the picker is never empty.
    enumerated = [];
  }

  // UNION, not just the enumerated list. ICU builds disagree about which name
  // is canonical: this runtime enumerates "Asia/Calcutta" and omits both
  // "Asia/Kolkata" and "UTC", yet accepts all three. Listing only what is
  // enumerated would therefore hide IST and UTC — two of the three zones the
  // requirement names by hand — on some machines and not others.
  // Everything is validated, so an alias for a zone this runtime does not know
  // is dropped rather than offered and then failing at format time.
  const named = ['UTC', ...Object.keys(BUILT_IN_TZ_ALIASES), ...Object.keys(defaults?.timeZoneAliases ?? {})];
  const zones = [...new Set([...named, ...enumerated])].filter(isValidZone);

  return zones
    .map((zone) => {
      let offsetMinutes = 0;
      try {
        offsetMinutes = dayjs().tz(zone).utcOffset();
      } catch {
        return null;
      }
      const alias = aliasFor(zone, defaults);
      const offsetLabel = zoneOffsetLabel(zone);
      return {
        value: zone,
        alias,
        offsetLabel,
        offsetMinutes,
        label: `${alias ? `${alias} — ` : ''}${zone} (${offsetLabel})`,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.offsetMinutes - b.offsetMinutes || a.value.localeCompare(b.value));
}
